import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL } from "../config";

const getDateKey = (date) => {
  return date.toLocaleDateString("en-CA");
};

const calcPoints = (item) =>
  (item.vyuctovaniCount || 0) * 2 + (item.nadiktovaneCount || 0) * 1;

const rankColors = [
  { bg: "bg-yellow-50 dark:bg-yellow-900/20", text: "text-yellow-600 dark:text-yellow-400", medal: "🥇" },
  { bg: "bg-gray-100 dark:bg-gray-700/40", text: "text-gray-500 dark:text-gray-300", medal: "🥈" },
  { bg: "bg-orange-50 dark:bg-orange-900/20", text: "text-orange-500 dark:text-orange-400", medal: "🥉" },
];

const getRangeDates = (range) => {
  const now = new Date();
  const todayKey = getDateKey(now);

  if (range === "today") {
    return { from: todayKey, to: todayKey };
  }

  if (range === "week") {
    const day = now.getDay();
    const diffToMonday = (day + 6) % 7; // 0=Mon
    const start = new Date(now);
    start.setDate(now.getDate() - diffToMonday);
    return { from: getDateKey(start), to: todayKey };
  }

  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: getDateKey(startOfMonth), to: todayKey };
};

const UserStatsWidget = ({ currentUser, userRole, isDarkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useState("today");
  const [stats, setStats] = useState([]);
  const [tableStats, setTableStats] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [historyMode, setHistoryMode] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [resetDay, setResetDay] = useState(() => getDateKey(new Date()));
  const [historyFrom, setHistoryFrom] = useState(() => {
    const now = new Date();
    return getDateKey(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [historyTo, setHistoryTo] = useState(() => getDateKey(new Date()));

  // Duelový systém
  const [challengeMode, setChallengeMode] = useState(false); // zobrazit výběr uživatele
  const [outgoingDuel, setOutgoingDuel] = useState(null);    // výzva, kterou jsem poslal
  const [isSendingChallenge, setIsSendingChallenge] = useState(false);
  const [allUsers, setAllUsers] = useState([]);              // všichni uživatelé bez callcentra
  const [challengeSearch, setChallengeSearch] = useState(""); // vyhledávání soupeře

  // Historie vítězství
  const [winnersMode, setWinnersMode] = useState(false);
  const [winnersData, setWinnersData] = useState([]);
  const [winnersLoading, setWinnersLoading] = useState(false);

  const activeRange = useMemo(() => {
    if (historyMode && userRole === "admin") {
      return { from: historyFrom, to: historyTo };
    }
    return getRangeDates(range);
  }, [historyMode, historyFrom, historyTo, range, userRole]);

  const mergeStats = useCallback((assignmentData, documentData, ccSet) => {
    const map = new Map();

    assignmentData.forEach((item) => {
      map.set(String(item.userId), {
        userId: item.userId,
        userName: item.userName || "Neznámý",
        assignedCount: item.totalCount || 0,
        vyuctovaniCount: 0,
        nadiktovaneCount: 0,
        isCallCenter: ccSet ? ccSet.has(String(item.userId)) : false,
      });
    });

    documentData.forEach((item) => {
      const key = String(item.userId);
      const existing = map.get(key) || {
        userId: item.userId,
        userName: item.userName || "Neznámý",
        assignedCount: 0,
        vyuctovaniCount: 0,
        nadiktovaneCount: 0,
        isCallCenter: ccSet ? ccSet.has(String(item.userId)) : false,
      };

      map.set(key, {
        ...existing,
        userName: existing.userName || item.userName || "Neznámý",
        vyuctovaniCount: item.vyuctovaniCount || 0,
        nadiktovaneCount: item.nadiktovaneCount || 0,
      });
    });

    if (currentUser?._id && !map.has(String(currentUser._id))) {
      map.set(String(currentUser._id), {
        userId: currentUser._id,
        userName: currentUser?.name || "Můj profil",
        assignedCount: 0,
        vyuctovaniCount: 0,
        nadiktovaneCount: 0,
        isCallCenter: false,
      });
    }

    const merged = Array.from(map.values());
    const filtered = merged
      .filter(
        (item) =>
          !item.isCallCenter &&
          ((item.assignedCount || 0) > 0 ||
            (item.vyuctovaniCount || 0) > 0 ||
            (item.nadiktovaneCount || 0) > 0)
      )
      .sort((a, b) => calcPoints(b) - calcPoints(a));

    setStats(merged);
    setTableStats(filtered);
  }, [currentUser]);

  const loadStats = useCallback(async () => {
    if (!currentUser?._id) return;

    setIsLoading(true);
    try {
      const uid = currentUser?._id || '';
      const [assignmentRes, documentRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/assignment-history-range?from=${activeRange.from}&to=${activeRange.to}&userId=${uid}`),
        fetch(`${API_URL}/document-counters/summary?from=${activeRange.from}&to=${activeRange.to}&userId=${uid}`),
        fetch(`${API_URL}/users?userId=${uid}`),
      ]);

      const assignmentData = assignmentRes.ok ? await assignmentRes.json() : [];
      const documentData = documentRes.ok ? await documentRes.json() : [];
      const usersData = usersRes.ok ? await usersRes.json() : [];

      const ccSet = new Set(
        usersData
          .filter((u) => u.role === "callcentrum" || u.role === "callcenter")
          .map((u) => String(u._id))
      );

      mergeStats(assignmentData, documentData, ccSet);
    } catch (error) {
      console.error("Chyba při načítání statistik:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeRange.from, activeRange.to, currentUser?._id, mergeStats]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const currentUserStats = useMemo(() => {
    if (!currentUser?._id) {
      return { assignedCount: 0, vyuctovaniCount: 0, nadiktovaneCount: 0 };
    }
    return (
      stats.find((item) => String(item.userId) === String(currentUser._id)) || {
        assignedCount: 0,
        vyuctovaniCount: 0,
        nadiktovaneCount: 0
      }
    );
  }, [currentUser?._id, stats]);

  const handleIncrement = async (type) => {
    if (!currentUser?._id) return;

    try {
      await fetch(`${API_URL}/document-counters/increment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: currentUser._id, type })
      });
      await loadStats();
    } catch (error) {
      console.error("Chyba při zapisování podkladů:", error);
    }
  };

  const handleReset = async (userId) => {
    if (!window.confirm(`Opravdu vynulovat podklady pro den ${resetDay}?`)) return;
    try {
      await fetch(`${API_URL}/document-counters/reset`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, dateKey: resetDay })
      });
      await loadStats();
    } catch (error) {
      console.error("Chyba při vynulování podkladů:", error);
    }
  };

  // Načtení všech uživatelů pro výběr soupeře
  const loadAllUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/users?userId=${currentUser?._id || ''}`);
      if (!res.ok) return;
      const data = await res.json();
      const filtered = data.filter(
        (u) =>
          u.role !== "callcentrum" &&
          u.role !== "callcenter" &&
          String(u._id) !== String(currentUser?._id)
      );
      setAllUsers(
        filtered.map((u) => ({ userId: u._id, userName: u.name || u.email || "Neznámý" }))
      );
    } catch (e) {
      // ignoruj
    }
  }, [currentUser?._id]);

  useEffect(() => {
    if (challengeMode) {
      setChallengeSearch("");
      loadAllUsers();
    }
  }, [challengeMode, loadAllUsers]);

  const loadWinners = useCallback(async () => {
    setWinnersLoading(true);
    try {
      const res = await fetch(`${API_URL}/document-counters/winners?userId=${currentUser?._id || ''}`);
      if (res.ok) setWinnersData(await res.json());
    } catch (e) { /* ignoruj */ }
    finally { setWinnersLoading(false); }
  }, [currentUser?._id]);

  useEffect(() => {
    if (winnersMode) loadWinners();
  }, [winnersMode, loadWinners]);

  // Odchozí duel – načítaj při každém otevření popup
  const loadOutgoingDuel = useCallback(async () => {
    if (!currentUser?._id) return;
    try {
      const res = await fetch(`${API_URL}/duels/outgoing?userId=${currentUser._id}`);
      if (res.ok) {
        const data = await res.json();
        setOutgoingDuel(data);
      }
    } catch (e) {
      // ignoruj
    }
  }, [currentUser?._id]);

  useEffect(() => {
    if (isOpen) loadOutgoingDuel();
  }, [isOpen, loadOutgoingDuel]);

  const handleChallenge = async (targetUser) => {
    if (!currentUser?._id) return;
    setIsSendingChallenge(true);
    try {
      const res = await fetch(`${API_URL}/duels`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: currentUser._id,
          challengerId: currentUser._id,
          challengerName: currentUser.name,
          challengedId: targetUser.userId,
          challengedName: targetUser.userName,
        }),
      });
      if (res.ok) {
        const duel = await res.json();
        setOutgoingDuel(duel);
      }
    } catch (e) {
      console.error("Chyba při odesílání výzvy:", e);
    } finally {
      setIsSendingChallenge(false);
      setChallengeMode(false);
    }
  };

  const handleCancelChallenge = async () => {
    if (!outgoingDuel?._id) return;
    try {
      await fetch(`${API_URL}/duels/${outgoingDuel._id}`, { method: "DELETE" });
      setOutgoingDuel(null);
    } catch (e) {
      console.error("Chyba při rušení výzvy:", e);
    }
  };

  // Vítěz = první v tabulce s body > 0, kdo není aktuální uživatel
  const winner = useMemo(() => {
    if (!tableStats.length) return null;
    const top = tableStats[0];
    if (calcPoints(top) === 0) return null;
    // Porovnání jako string aby se vyhlo problémům s ObjectId vs string
    if (String(top.userId) === String(currentUser?._id)) return null;
    return top;
  }, [tableStats, currentUser?._id]);

  const rangeLabel = useMemo(() => {
    if (historyMode && userRole === "admin") {
      return `Historie (${activeRange.from} – ${activeRange.to})`;
    }
    if (range === "today") return "Dnes";
    if (range === "week") return "Tento týden";
    return "Tento měsíc";
  }, [activeRange.from, activeRange.to, historyMode, range, userRole]);

  return (
    <>
      {/* ===== INLINE BAR – vkládán přímo do lišty stránkování přes prop ze App.js ===== */}
      <div
        className={`flex items-center gap-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-full overflow-visible h-12 ${isDarkMode ? "dark" : ""}`}
      >
        {/* Jméno + klik na detail */}
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center gap-1.5 pl-4 pr-3 h-full text-xs font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-l-full"
          title="Výkon obchodníků"
          style={{ minWidth: 0 }}
        >
          {winner ? (
            <>
              <span>🥇</span>
              <span className="truncate max-w-[110px] text-yellow-600 dark:text-yellow-400">
                {winner.userName}
              </span>
            </>
          ) : (
            <span>🏆</span>
          )}
        </button>

        <div className="w-px h-6 bg-gray-200 dark:bg-gray-600" />

        {/* Vyúčtování */}
        <button
          onClick={() => handleIncrement("vyuctovani")}
          className="relative flex items-center justify-center h-full w-12 bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          title="Přidat podklad (vyúčtování)"
        >
          <span className="text-sm">🧾</span>
          <span
            className="absolute bg-white text-blue-700 text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center border border-blue-200"
            style={{ top: "-5px", right: "-5px", zIndex: 20 }}
          >
            {currentUserStats.vyuctovaniCount || 0}
          </span>
        </button>

        {/* Nadiktované */}
        <button
          onClick={() => handleIncrement("nadiktovane")}
          className="relative flex items-center justify-center h-full w-12 bg-emerald-600 hover:bg-emerald-700 text-white transition-colors rounded-r-full"
          title="Přidat podklad (nadiktované)"
        >
          <span className="text-sm">🎙️</span>
          <span
            className="absolute bg-white text-emerald-700 text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center border border-emerald-200"
            style={{ top: "-5px", right: "-5px", zIndex: 20 }}
          >
            {currentUserStats.nadiktovaneCount || 0}
          </span>
        </button>
      </div>

      {/* ===== POPUP TABULKA ===== */}
      {isOpen && (
        <div
          className={`fixed z-[10002] ${isDarkMode ? "dark" : ""}`}
          style={{ left: "24px", bottom: "90px" }}
        >
          <div className="w-[460px] max-w-[95vw] max-h-[70vh] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
            {/* Hlavička */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Výkon</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{rangeLabel}</div>
              </div>
              {/* Tlačítka Vyzvat / Zrušit výzvu – uprostřed */}
              <div className="flex-1 flex justify-center items-center gap-2">
                <button
                  onClick={() => setChallengeMode((v) => !v)}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                    challengeMode
                      ? "bg-orange-500 text-white"
                      : "bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-300 border border-orange-200 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/50"
                  }`}
                  title="Vyzvat obchodníka na souboj"
                >
                  <span>⚔️</span>
                  <span>Vyzvat</span>
                </button>
                {outgoingDuel && (
                  <button
                    onClick={handleCancelChallenge}
                    className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                    title="Zrušit odeslanou výzvu"
                  >
                    <span>✕</span>
                    <span>Zrušit výzvu</span>
                    {outgoingDuel.accepted && <span className="text-green-600 dark:text-green-400 font-bold ml-1">✓</span>}
                  </button>
                )}
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                title="Zavřít"
              >
                ✕
              </button>
            </div>

            {/* Výběr soupeře */}
            {challengeMode && !outgoingDuel && (
              <div className="px-4 py-3 border-b border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20">
                <div className="text-xs font-semibold text-orange-700 dark:text-orange-300 mb-2">
                  ⚔️ Vyber soupeře:
                </div>
                {/* Vyhledávání */}
                <input
                  type="text"
                  value={challengeSearch}
                  onChange={(e) => setChallengeSearch(e.target.value)}
                  placeholder="Hledat jméno..."
                  autoFocus
                  className="w-full mb-2 px-3 py-1.5 text-xs rounded-lg border border-orange-200 dark:border-orange-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
                />
                <div className="flex flex-col gap-1.5 max-h-44 overflow-y-auto">
                  {(() => {
                    const query = challengeSearch.trim().toLowerCase();
                    const visible = allUsers.filter(
                      (u) => !query || u.userName.toLowerCase().includes(query)
                    );
                    if (visible.length === 0) {
                      return (
                        <div className="text-xs text-gray-500 dark:text-gray-400 py-1">
                          {allUsers.length === 0 ? "Načítám..." : "Žádný uživatel nevyhovuje hledání."}
                        </div>
                      );
                    }
                    return visible.map((u) => {
                      const statsEntry = tableStats.find(
                        (t) => String(t.userId) === String(u.userId)
                      );
                      const pts = statsEntry ? calcPoints(statsEntry) : null;
                      return (
                        <button
                          key={u.userId}
                          onClick={() => handleChallenge(u)}
                          disabled={isSendingChallenge}
                          className="flex items-center justify-between px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-orange-200 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors text-xs text-gray-800 dark:text-gray-100 disabled:opacity-60"
                        >
                          <span className="font-semibold">{u.userName}</span>
                          {pts !== null && (
                            <span className="text-orange-600 dark:text-orange-400 font-bold">{pts} bodů</span>
                          )}
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Filtry */}
            <div className="px-4 py-2 flex flex-col gap-2 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { id: "today", label: "Dnes" },
                  { id: "week", label: "Týden" },
                  { id: "month", label: "Měsíc" },
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setHistoryMode(false);
                      setAdminOpen(false);
                      setWinnersMode(false);
                      setRange(item.id);
                    }}
                    className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                      range === item.id && !historyMode && !winnersMode
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}

                <button
                  onClick={() => {
                    setWinnersMode((v) => !v);
                    setAdminOpen(false);
                    setHistoryMode(false);
                  }}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                    winnersMode
                      ? "bg-yellow-500 text-white"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  🏆 Výhry
                </button>

                {userRole === "admin" && (
                  <button
                    onClick={() => { setAdminOpen((v) => !v); setWinnersMode(false); }}
                    className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                      adminOpen || historyMode
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                    }`}
                  >
                    Admin
                  </button>
                )}
              </div>

              {/* Admin – výběr rozsahu dat + den pro reset */}
              {userRole === "admin" && adminOpen && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={historyFrom}
                      onChange={(e) => {
                        setHistoryFrom(e.target.value);
                        setHistoryMode(true);
                      }}
                      className="flex-1 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    />
                    <span className="text-xs text-gray-500">→</span>
                    <input
                      type="date"
                      value={historyTo}
                      onChange={(e) => {
                        setHistoryTo(e.target.value);
                        setHistoryMode(true);
                      }}
                      className="flex-1 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Vynulovat den:</span>
                    <input
                      type="date"
                      value={resetDay}
                      onChange={(e) => setResetDay(e.target.value)}
                      className="flex-1 px-2 py-1 text-xs border border-red-200 dark:border-red-700 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                    />
                  </div>
                </div>
              )}

              {/* Legenda bodů */}
              <div className="flex gap-4 text-[11px] text-gray-500 dark:text-gray-400">
                <span>🎙️ Nadiktované podklady = 1 bod</span>
                <span>🧾 Získané vyúčtování = 2 body</span>
              </div>
            </div>

            {/* Tabulka / Historie vítězství */}
            <div className="flex-1 overflow-auto">
              {winnersMode ? (
                winnersLoading ? (
                  <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Načítám...</div>
                ) : winnersData.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Zatím žádná vítězství.</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                      <tr>
                        <th className="text-left font-semibold px-3 py-2">Obchodník</th>
                        <th className="text-right font-semibold px-3 py-2">Vítězství</th>
                      </tr>
                    </thead>
                    <tbody>
                      {winnersData.map((item, index) => {
                        const medal = index === 0 ? "🥇" : index === 1 ? "🥈" : index === 2 ? "🥉" : "";
                        return (
                          <tr key={item.userId} className="border-b border-gray-100 dark:border-gray-700">
                            <td className="px-3 py-2 font-semibold text-gray-700 dark:text-gray-200">
                              {medal && <span className="mr-1">{medal}</span>}{item.userName}
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-yellow-600 dark:text-yellow-400">
                              {item.wins}x
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              ) : isLoading ? (
                <div className="p-4 text-sm text-gray-500 dark:text-gray-400">Načítám data...</div>
              ) : tableStats.length === 0 ? (
                <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
                  Zatím nikdo nepřidal podklady ani nepřidělil kontakty.
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-300">
                    <tr>
                      <th className="text-left font-semibold px-3 py-2">Obchodník</th>
                      <th className="text-right font-semibold px-2 py-2">Přiděleno</th>
                      <th className="text-right font-semibold px-2 py-2">Vyúčtování</th>
                      <th className="text-right font-semibold px-2 py-2">Nadiktované</th>
                      <th className="text-right font-semibold px-2 py-2">Body</th>
                      {userRole === "admin" && adminOpen && (
                        <th className="text-center font-semibold px-2 py-2">Reset</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {tableStats.map((item, index) => {
                      const points = calcPoints(item);
                      const color = points > 0 ? (rankColors[index] || null) : null;
                      return (
                        <tr
                          key={item.userId}
                          className={`border-b border-gray-100 dark:border-gray-700 ${
                            color ? color.bg : ""
                          }`}
                        >
                          <td className={`px-3 py-2 font-semibold truncate max-w-[120px] ${
                            color ? color.text : "text-gray-700 dark:text-gray-200"
                          }`}>
                            {color ? `${color.medal} ` : ""}{item.userName || "Neznámý"}
                          </td>
                          <td className="px-2 py-2 text-right text-gray-600 dark:text-gray-300">{item.assignedCount || 0}</td>
                          <td className="px-2 py-2 text-right text-gray-600 dark:text-gray-300">{item.vyuctovaniCount || 0}</td>
                          <td className="px-2 py-2 text-right text-gray-600 dark:text-gray-300">{item.nadiktovaneCount || 0}</td>
                          <td className={`px-2 py-2 text-right font-bold ${
                            color ? color.text : "text-gray-700 dark:text-gray-200"
                          }`}>
                            {points}
                          </td>
                          {userRole === "admin" && adminOpen && (
                            <td className="px-2 py-2 text-center">
                              <button
                                onClick={() => handleReset(item.userId)}
                                className="text-red-400 hover:text-red-600 text-xs px-1.5 py-0.5 rounded border border-red-200 hover:border-red-400 transition-colors"
                                title={`Vynulovat podklady pro ${item.userName} za ${resetDay}`}
                              >
                                0
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserStatsWidget;

// ===== MiniCounterButtons – jen tlačítka s počítadly (bez jména), pro kartu společnosti =====
export const MiniCounterButtons = ({ userId, isDarkMode }) => {
  const todayKey = getDateKey(new Date());
  const [counts, setCounts] = useState({ vyuctovaniCount: 0, nadiktovaneCount: 0 });

  const loadCounts = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/document-counters/summary?from=${todayKey}&to=${todayKey}&userId=${userId}`);
      if (!res.ok) return;
      const data = await res.json();
      const mine = data.find((d) => String(d.userId) === String(userId));
      if (mine) setCounts({ vyuctovaniCount: mine.vyuctovaniCount || 0, nadiktovaneCount: mine.nadiktovaneCount || 0 });
    } catch (e) { /* ignoruj */ }
  }, [userId, todayKey]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  const handleIncrement = async (type) => {
    if (!userId) return;
    const field = type === "vyuctovani" ? "vyuctovaniCount" : "nadiktovaneCount";
    setCounts((prev) => ({ ...prev, [field]: (prev[field] || 0) + 1 }));
    try {
      await fetch(`${API_URL}/document-counters/increment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, type }),
      });
    } catch (e) { /* ignoruj */ }
  };

  return (
    <div className={`flex items-center gap-0 ${isDarkMode ? "dark" : ""}`} style={{ overflow: "visible", position: "relative" }}>
      <button
        type="button"
        onClick={() => handleIncrement("vyuctovani")}
        className="relative flex items-center justify-center h-9 w-10 bg-blue-600 hover:bg-blue-700 text-white transition-colors rounded-l-full"
        title="Přidat podklad (vyúčtování)"
      >
        <span className="text-sm">🧾</span>
        <span
          className="absolute bg-white text-blue-700 text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center border border-blue-200"
          style={{ top: "-5px", right: "-5px", zIndex: 50 }}
        >
          {counts.vyuctovaniCount}
        </span>
      </button>
      <button
        type="button"
        onClick={() => handleIncrement("nadiktovane")}
        className="relative flex items-center justify-center h-9 w-10 bg-emerald-600 hover:bg-emerald-700 text-white transition-colors rounded-r-full"
        title="Přidat podklad (nadiktované)"
        style={{ marginRight: "8px" }}
      >
        <span className="text-sm">🎙️</span>
        <span
          className="absolute bg-white text-emerald-700 text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center border border-emerald-200"
          style={{ top: "-5px", right: "-5px", zIndex: 50 }}
        >
          {counts.nadiktovaneCount}
        </span>
      </button>
    </div>
  );
};

// ===== DuelBar – zobrazí se v pagination baru jako pozvánka nebo aktivní duel =====
export const DuelBar = ({ currentUser, isDarkMode }) => {
  const [incomingDuels, setIncomingDuels] = useState([]);
  const [outgoingDuel, setOutgoingDuel] = useState(null);
  const [duelIndex, setDuelIndex] = useState(0);
  // live skóre per duel: { [duelId]: { myPoints, opponentPoints } }
  const [liveScores, setLiveScores] = useState({});

  const todayKey = getDateKey(new Date());

  const loadDuels = useCallback(async () => {
    if (!currentUser?._id) return;
    try {
      const [inRes, outRes] = await Promise.all([
        fetch(`${API_URL}/duels/incoming?userId=${currentUser._id}`),
        fetch(`${API_URL}/duels/outgoing?userId=${currentUser._id}`),
      ]);
      const inData = inRes.ok ? await inRes.json() : [];
      const out = outRes.ok ? await outRes.json() : null;
      const todayIncoming = Array.isArray(inData) ? inData.filter((d) => d.dateKey === todayKey) : [];
      const todayOut = out && out.dateKey === todayKey ? out : null;
      setIncomingDuels(todayIncoming);
      setOutgoingDuel(todayOut);
      const newTotal = todayIncoming.length + (todayOut ? 1 : 0);
      setDuelIndex((prev) => Math.min(prev, Math.max(0, newTotal - 1)));
    } catch (e) { /* ignoruj */ }
  }, [currentUser?._id, todayKey]);

  // Unified seznam: incoming + outgoing (outgoing je vždy poslední)
  const allDuels = useMemo(() => {
    const list = [...incomingDuels];
    if (outgoingDuel) list.push({ ...outgoingDuel, _isOutgoing: true });
    return list;
  }, [incomingDuels, outgoingDuel]);

  const total = allDuels.length;
  const currentDuel = allDuels[duelIndex] || null;

  // Načti live skóre pro VŠECHNY přijaté duely najednou
  const loadAllLiveScores = useCallback(async () => {
    const accepted = allDuels.filter((d) => d.accepted);
    if (!accepted.length || !currentUser?._id) return;
    try {
      const res = await fetch(`${API_URL}/document-counters/summary?from=${todayKey}&to=${todayKey}&userId=${currentUser?._id || ''}`);
      if (!res.ok) return;
      const data = await res.json();
      const myEntry = data.find((d) => String(d.userId) === String(currentUser._id));
      const myPts = myEntry ? calcPoints(myEntry) : 0;
      const scores = {};
      for (const duel of accepted) {
        const isChallenger = String(currentUser._id) === String(duel.challengerId);
        const opponentId = isChallenger ? String(duel.challengedId) : String(duel.challengerId);
        const opEntry = data.find((d) => String(d.userId) === opponentId);
        scores[String(duel._id)] = { myPoints: myPts, opponentPoints: opEntry ? calcPoints(opEntry) : 0 };
      }
      setLiveScores(scores);
    } catch (e) { /* ignoruj */ }
  }, [allDuels, currentUser?._id, todayKey]);

  useEffect(() => {
    loadDuels();
    const iv = setInterval(loadDuels, 20000);
    return () => clearInterval(iv);
  }, [loadDuels]);

  useEffect(() => {
    const hasAccepted = allDuels.some((d) => d.accepted);
    if (!hasAccepted) return;
    loadAllLiveScores();
    const iv = setInterval(loadAllLiveScores, 15000);
    return () => clearInterval(iv);
  }, [allDuels, loadAllLiveScores]);

  const handleAccept = async () => {
    if (!currentDuel?._id || currentDuel._isOutgoing) return;
    try {
      const res = await fetch(`${API_URL}/duels/${currentDuel._id}/accept`, { method: "POST" });
      if (res.ok) {
        const updated = await res.json();
        setIncomingDuels((prev) => prev.map((d) => d._id === updated._id ? updated : d));
      }
    } catch (e) { console.error("Chyba při přijímání výzvy:", e); }
  };

  const handleDecline = async () => {
    if (!currentDuel?._id || currentDuel._isOutgoing) return;
    try {
      await fetch(`${API_URL}/duels/${currentDuel._id}`, { method: "DELETE" });
      setIncomingDuels((prev) => prev.filter((d) => d._id !== currentDuel._id));
      setDuelIndex((prev) => Math.max(0, prev - 1));
    } catch (e) { console.error("Chyba při odmítnutí:", e); }
  };

  const handleCancelOutgoing = async () => {
    if (!outgoingDuel?._id) return;
    try {
      await fetch(`${API_URL}/duels/${outgoingDuel._id}`, { method: "DELETE" });
      setOutgoingDuel(null);
      setDuelIndex((prev) => Math.max(0, prev - 1));
    } catch (e) { console.error("Chyba při rušení výzvy:", e); }
  };

  if (!total || !currentDuel) return null;

  const Switcher = ({ divColor }) => total <= 1 ? null : (
    <>
      <div className={`w-px h-5 ${divColor} mx-1`} />
      <button onClick={() => setDuelIndex((i) => (i - 1 + total) % total)} className="font-bold px-0.5 leading-none hover:opacity-70">‹</button>
      <span className="text-[10px] tabular-nums">{duelIndex + 1}/{total}</span>
      <button onClick={() => setDuelIndex((i) => (i + 1) % total)} className="font-bold px-0.5 leading-none hover:opacity-70">›</button>
    </>
  );

  // Příchozí nevyřízená výzva
  if (!currentDuel._isOutgoing && !currentDuel.accepted) {
    return (
      <div className={`flex items-center gap-2 px-3 h-12 rounded-full border shadow-lg text-xs font-semibold ${isDarkMode ? "dark" : ""} bg-yellow-50 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-600`}>
        <span>⚔️</span>
        <span className="text-yellow-800 dark:text-yellow-200">
          <strong>{currentDuel.challengerName}</strong> Vás vyzývá!
        </span>
        <button onClick={handleAccept} className="px-2 py-0.5 bg-green-500 hover:bg-green-600 text-white rounded-full transition-colors" title="Přijmout výzvu">Přijmout</button>
        <button onClick={handleDecline} className="px-2 py-0.5 bg-red-400 hover:bg-red-500 text-white rounded-full transition-colors" title="Odmítnout výzvu">✕</button>
        <Switcher divColor="bg-yellow-300 dark:bg-yellow-600" />
      </div>
    );
  }

  // Přijatý duel (incoming nebo outgoing)
  if (currentDuel.accepted) {
    const scores = liveScores[String(currentDuel._id)] || { myPoints: 0, opponentPoints: 0 };
    const isChallenger = String(currentUser._id) === String(currentDuel.challengerId);
    const myName = currentUser.name || "Já";
    const opponentName = isChallenger ? currentDuel.challengedName : currentDuel.challengerName;
    const iWin = scores.myPoints >= scores.opponentPoints;
    return (
      <div className={`flex items-center gap-2 px-4 h-12 rounded-full border shadow-lg text-xs font-semibold ${isDarkMode ? "dark" : ""} bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-700`}>
        <span className="text-orange-700 dark:text-orange-300">⚔️</span>
        <span className={iWin ? "text-green-600 dark:text-green-400" : "text-gray-700 dark:text-gray-200"}>
          {myName}: <strong>{scores.myPoints}b</strong>
        </span>
        <span className="text-gray-400">vs</span>
        <span className={!iWin ? "text-green-600 dark:text-green-400" : "text-gray-700 dark:text-gray-200"}>
          {opponentName}: <strong>{scores.opponentPoints}b</strong>
        </span>
        <Switcher divColor="bg-orange-200 dark:bg-orange-700" />
      </div>
    );
  }

  // Odchozí výzva čekající na přijetí soupeřem
  return (
    <div className={`flex items-center gap-2 px-3 h-12 rounded-full border shadow-lg text-xs font-semibold ${isDarkMode ? "dark" : ""} bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-600`}>
      <span>⏳</span>
      <span className="text-blue-800 dark:text-blue-200">
        Čeká na přijetí: <strong>{currentDuel.challengedName}</strong>
      </span>
      <button onClick={handleCancelOutgoing} className="px-2 py-0.5 bg-gray-400 hover:bg-gray-500 text-white rounded-full transition-colors" title="Zrušit výzvu">✕</button>
      <Switcher divColor="bg-blue-300 dark:bg-blue-600" />
    </div>
  );
};
